import React, { useEffect, useState,PropsWithChildren} from "react";
import { IonContent, IonItem, IonImg, IonLoading, IonGrid,IonModal, IonRow, IonCol, IonButton, IonIcon} from '@ionic/react';
import './LockScreenModal.css';
import { useTranslation } from 'react-i18next';
import { useHistory } from "react-router-dom";
import { useAppDispatch, useAppSelector } from '../../services/redux/hook';
import KeyPad from "../../components/KeyPad/KeyPad";
import cn from 'classnames';
import {  WalletInfoState } from "../../types/types";
import { Networks } from "@stellar/stellar-sdk";
import { setWalletInfo } from "../../services/redux/slices/walletInfo/wallet";
import { setPIN } from "../../services/redux/slices/pin/pin";
import { KeyManager, KeyManagerPlugins } from "../../services/stellar/wallet-sdk";
import { CapacitorKeyStore } from "../../services/stellar/wallet-sdk/plugins/CapacitorStorage";
import { Haptics } from "@capacitor/haptics";
import BiometricSecurity from '../../services/security/biometricSecurity';
import { Capacitor } from '@capacitor/core';
import { useDeviceStorage } from '../../services/device_storage/useDeviceStorage';


const isTestnet = import.meta.env.VITE_isTestnet === 'true' ? true : false;

interface Props extends PropsWithChildren {
    isOpen: boolean;
    onUnlock: ()=>void;
}

const LockScreenModal: React.FC<Props> = ({isOpen,onUnlock}) => {

    const dispatch = useAppDispatch();
    const [isIncorrect, setisIncorrect] = useState(false);
    const { t, i18n } = useTranslation();
    const [pins, setPins] = React.useState(['', '', '', '', '', '']);
    const [showLoading, setShowLoading] = useState(false);
    const [biometricAvailable, setBiometricAvailable] = useState(false);
    const [hasTriedBiometric, setHasTriedBiometric] = useState(false);
    const [biometricFailed, setBiometricFailed] = useState(false);
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const { get: getStorage } = useDeviceStorage();
    
    const walletInfo: WalletInfoState = useAppSelector((state) => state.walletInfo);
    
    const keyManagerStorage = new CapacitorKeyStore();
    const keyManager = new KeyManager({ keyStore: keyManagerStorage });
    keyManager.registerEncrypter(KeyManagerPlugins.ScryptEncrypter);
    keyManager.setDefaultNetworkPassphrase(isTestnet ? Networks.TESTNET : Networks.PUBLIC);

    // Check if biometric authentication is available and trigger automatic authentication
    useEffect(() => {
        const checkBiometricAvailability = async () => {
            if (!isOpen) return; // Only check when modal is open

            const biometricEnabled = await getStorage('biometric_enabled');

            if (!biometricEnabled || biometricEnabled === 'false'){
                return;
            }
            // Skip if running in browser
            if (Capacitor.getPlatform() === 'web') {
                return;
            }
            
            const biometricSecurity = BiometricSecurity.getInstance();
            const result = await biometricSecurity.isBiometricAvailable();
            
            setBiometricAvailable(result.isAvailable);
            
            // If biometrics are available and we haven't tried yet, attempt automatic authentication
            if (result.isAvailable && !hasTriedBiometric) {
                setHasTriedBiometric(true);
                await tryBiometricAuthentication();
            }
        };
        
        checkBiometricAvailability();
    }, [isOpen, hasTriedBiometric, getStorage]);

    // Reset states when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setHasTriedBiometric(false);
            setBiometricFailed(false);
            setIsAuthenticating(false);
            setPins(['', '', '', '', '', '']);
        }
    }, [isOpen]);

    // Attempt biometric authentication automatically
    const tryBiometricAuthentication = async () => {
        try {
            setIsAuthenticating(true);
            const biometricSecurity = BiometricSecurity.getInstance();
            
            // Check if we have stored credentials for the wallet PIN
            const credentialsResult = await biometricSecurity.getCredentials('wallet_pin');
            
            if (credentialsResult.success && credentialsResult.password) {
                // We have stored PIN, now authenticate with biometrics
                const verifyResult = await biometricSecurity.verifyIdentity({
                    title: t('biometric.auth.TITLE') ?? "",
                    subtitle: t('biometric.auth.SUBTITLE') ?? "",
                    description: t('biometric.auth.DESCRIPTION') ?? ""
                });
                
                if (verifyResult.verified) {
                    // Use the retrieved PIN to unlock the wallet
                    await authenticateWithPin(credentialsResult.password);
                    return;
                }
            }
        } catch (error) {
            console.log('Biometric authentication failed or cancelled:', error);
            setBiometricFailed(true);
            // Continue with PIN entry
        } finally {
            setIsAuthenticating(false);
        }
    };

    // Authenticate with PIN (extracted for reuse in biometric flow)
    const authenticateWithPin = async (inputCombination: string) => {
        setShowLoading(true);

        try {
            const account_key = await keyManager.loadKey("account_key", inputCombination);
            const device_key = await keyManager.loadKey("device_key", inputCombination);

            const newWalletInfo = { ...walletInfo };
            newWalletInfo.account_key = account_key.publicKey;
            newWalletInfo.account_private_key = account_key.privateKey;
            newWalletInfo.device_key = device_key.publicKey;
            newWalletInfo.device_private_key = device_key.privateKey;
            newWalletInfo.has_key = true;
            
            dispatch(setWalletInfo(newWalletInfo));
            dispatch(setPIN(inputCombination));
            setPins(['', '', '', '', '', '']);
            setShowLoading(false);

            onUnlock();
        } catch (error) {
            setShowLoading(false);
            setisIncorrect(true);
            await Haptics.vibrate({duration: 800});
            setTimeout(() => {
                setPins(['', '', '', '', '', '']);
                setisIncorrect(false);
            }, 800);
        }
    };

    const modelChange = async () => {
        if (pins.length === 6) {
            const inputCombination = pins.map((i) => Number(i)).join("");
            await authenticateWithPin(inputCombination);
        }
    };

    // Handle biometric authentication (for manual button press)
    const handleBiometricAuth = async () => {
        try {
            setShowLoading(true);
            const biometricSecurity = BiometricSecurity.getInstance();
            
            // First verify identity with biometric
            const verifyResult = await biometricSecurity.verifyIdentity({
                title: t('biometric.auth.TITLE') ?? "",
                subtitle: t('biometric.auth.SUBTITLE') ?? "",
                description: t('biometric.auth.DESCRIPTION') ?? ""
            });
            
            if (!verifyResult.verified) {
                setShowLoading(false);
                return;
            }
            
            // Get stored credentials (PIN)
            const credentials = await biometricSecurity.getCredentials('wallet_pin');
            
            if (!credentials.success || !credentials.password) {
                setShowLoading(false);
                return;
            }
            
            // Use the retrieved PIN to authenticate
            await authenticateWithPin(credentials.password);
        } catch (error) {
            console.error('Biometric authentication error:', error);
            setShowLoading(false);
        }
    };


    const handleClick = (value: any) => {

        let tempCode = pins;

        for (var i = 0; i < tempCode.length; i++) {
            if (tempCode[i] == '') {
                tempCode[i] = value
                break;
            }
            else {
                continue
            }
        }
        setPins([...tempCode])
    }

    const handleRemove = () => {

        let tempCode = pins;
        for (let i = tempCode.length - 1; i >= 0; i--) {
            if (tempCode[i] != '') {
                tempCode[i] = '';
                break
            }
            else {
                continue
            }
        }
        setPins([...tempCode])


    }

    useEffect(() => {
        let count = 0
        
        for (let i = 0; i < pins.length; i++) {
            if (pins[i] != '') {
                count += 1
            }
        }
        if (count === 6) {
            modelChange()
        }
    }, [pins])

   
    return (
        <IonModal isOpen={isOpen}>
            <IonContent scrollY={false}>
                <IonLoading isOpen={showLoading} />
                
                <IonItem lines="none" className="lock-screen-logo-container">
                    <IonImg className="lock-screen-logo-img" src="/assets/img/ebioro-symbol-darkblue.svg" />
                </IonItem>

                <div className="lock-screen-header ion-padding">
                    <h4 className="ion-text-center">{t('pin.lock-screen.GREETING_HEADER')}</h4>
                    {isAuthenticating && (
                        <p className="biometric-message biometric-message--authenticating">
                            {t('biometric.authenticating') || 'Authenticating with biometrics...'}
                        </p>
                    )}
                    {biometricFailed && !isAuthenticating && (
                        <p className="biometric-message biometric-message--failed">
                            {t('biometric.fallback_to_pin') || 'Please enter your PIN to continue'}
                        </p>
                    )}
                </div>

                <IonGrid className="lock-screen-PIN">
                    <IonRow>
                        {pins.map((value, index) => (
                            <IonCol size="2" key={index} className="ion-text-center">
                                <div className={cn({ "animated-shake": isIncorrect })}>
                                    <div className={`${value !== '' ? 'lock-screen-otp-input__active' : 'lock-screen-otp-input'}`} />
                                </div>
                            </IonCol>
                        ))}
                    </IonRow>
                </IonGrid>

                <IonGrid className="lock-screen-pinKeyPad">
                    <KeyPad handleClick={handleClick} handleRemove={handleRemove} compact />
                </IonGrid>

            </IonContent>
        </IonModal>
    )

}

export default LockScreenModal
